import React, { Suspense } from 'react';
import { BrowserRouter as Router, Routes, Route, useLocation } from 'react-router-dom';
import { LanguageProvider } from './contexts/LanguageContext';
import { Header } from './components/Header';
import { Footer } from './components/Footer';
import { LoadingScreen } from './components/LoadingScreen';

// Code Splitting: Lazy load route components
// Only the Header and Footer load immediately (shared layout).
// Each page is a separate JS chunk that downloads on navigation.
const HomePage = React.lazy(() => import('./components/HomePage').then(m => ({ default: m.HomePage })));
const AboutPage = React.lazy(() => import('./components/AboutPage').then(m => ({ default: m.AboutPage })));
const BlogPage = React.lazy(() => import('./components/BlogPage').then(m => ({ default: m.BlogPage })));
const BlogArticlePage = React.lazy(() => import('./components/BlogArticlePage').then(m => ({ default: m.BlogArticlePage })));
const MultimediaPage = React.lazy(() => import('./components/MultimediaPage').then(m => ({ default: m.MultimediaPage })));
const ContactPage = React.lazy(() => import('./components/ContactPage').then(m => ({ default: m.ContactPage })));
// Clerk: desde el 2026-10-29 su página es clerk.do, y el Worker manda /mcp
// allí antes de llegar al SPA. Esta página vieja solo la abre el arnés del
// tutorial; la MISMA ruta, por POST, la atiende el Worker del MCP.
const ClerkPage = React.lazy(() => import('./components/ClerkPage').then(m => ({ default: m.ClerkPage })));
const PrivacidadClerkPage = React.lazy(() => import('./components/PrivacidadClerkPage').then(m => ({ default: m.PrivacidadClerkPage })));
const PrivacyPage = React.lazy(() => import('./components/PrivacyPage').then(m => ({ default: m.PrivacyPage })));
const AvisoLegalPage = React.lazy(() => import('./components/AvisoLegalPage').then(m => ({ default: m.AvisoLegalPage })));
const CookiesPage = React.lazy(() => import('./components/CookiesPage').then(m => ({ default: m.CookiesPage })));
const PrivacidadExtensionPage = React.lazy(() => import('./components/PrivacidadExtensionPage').then(m => ({ default: m.PrivacidadExtensionPage })));
const ReembolsosPage = React.lazy(() => import('./components/ReembolsosPage').then(m => ({ default: m.ReembolsosPage })));
const CondicionesCorporativoPage = React.lazy(() => import('./components/CondicionesCorporativoPage').then(m => ({ default: m.CondicionesCorporativoPage })));

// Ensure scroll position resets to top on route change
//
// ⚠️ Salvo que la URL traiga ancla (`/mcp#clerk-conectar`, a donde manda el
// atajo `/clerk`). El navegador salta al ancla al cargar, pero aquí no hay nada
// a lo que saltar todavía: la página es perezosa y React la pinta después. Y
// este efecto, además, mandaba arriba. Se espera a que exista la sección (hasta
// 3 s) y se baja a ella.
const ScrollToTop: React.FC = () => {
  const location = useLocation();

  React.useEffect(() => {
    const id = decodeURIComponent(location.hash.slice(1));
    if (!id) {
      window.scrollTo({ top: 0, left: 0, behavior: 'auto' });
      return;
    }
    let vivo = true;
    const limite = Date.now() + 3000;
    // ⚠️ Saltar una vez no basta: lo que hay ENCIMA de la sección (fuentes,
    // portadas de video, piezas perezosas) sigue cambiando de alto después, y
    // la sección acababa fuera de la pantalla (-12 px en el CI). Durante unos
    // segundos se vuelve a alinear cada vez que la página cambia de alto,
    // salvo que la persona ya se haya movido por su cuenta.
    let observador: ResizeObserver | undefined;
    const soltar = () => { vivo = false; observador?.disconnect(); quitarEscuchas(); };
    const eventos = ['wheel', 'touchstart', 'keydown', 'mousedown'] as const;
    const quitarEscuchas = () => eventos.forEach((e) => window.removeEventListener(e, soltar));
    const buscar = () => {
      if (!vivo) return;
      const destino = document.getElementById(id);
      if (!destino) {
        if (Date.now() < limite) window.requestAnimationFrame(buscar);
        return;
      }
      const alinear = () => { if (vivo) destino.scrollIntoView({ block: 'start', behavior: 'auto' }); };
      alinear();
      eventos.forEach((e) => window.addEventListener(e, soltar, { passive: true }));
      observador = new ResizeObserver(alinear);
      observador.observe(document.body);
      document.fonts?.ready.then(alinear);
      window.setTimeout(soltar, 4000);
    };
    buscar();
    return soltar;
  }, [location.pathname, location.hash]);

  return null;
};

// Prevent images from being saved via right-click
const ImageProtection: React.FC = () => {
  React.useEffect(() => {
    const handler = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      if (target?.tagName === 'IMG') e.preventDefault();
    };
    document.addEventListener('contextmenu', handler);
    return () => document.removeEventListener('contextmenu', handler);
  }, []);
  return null;
};

export default function App() {
  return (
    <LanguageProvider>
      <Router>
        <div className="min-h-screen bg-background flex flex-col">
          <ImageProtection />
          <Header />
          <main className="pt-0 flex-1">
            <ScrollToTop />
            <Suspense fallback={<LoadingScreen />}>
              <Routes>
                <Route path="/" element={<HomePage />} />
                <Route path="/about" element={<AboutPage />} />
                <Route path="/blog" element={<BlogPage />} />
                <Route path="/blog/:articleId" element={<BlogArticlePage />} />
                <Route path="/multimedia" element={<MultimediaPage />} />
                <Route path="/contact" element={<ContactPage />} />
                <Route path="/mcp" element={<ClerkPage />} />
                <Route path="/privacidad" element={<PrivacidadClerkPage />} />
                {/* ⚠️ Dos políticas distintas y las dos hacen falta:
                    /privacidad es la de CLERK y su URL está escrita en el
                    servidor MCP y en cada registro de consentimiento, así que
                    no se mueve; /privacy es la del SITIO. */}
                <Route path="/privacy" element={<PrivacyPage />} />
                {/* Aviso legal del sitio y condiciones de uso de Clerk, en UN
                    documento y UNA URL: es la que registra el consentimiento. */}
                <Route path="/aviso-legal" element={<AvisoLegalPage />} />
                {/* Política de reembolsos de Clerk. Desarrolla la sección 16
                    de las condiciones, no la cambia. */}
                <Route path="/reembolsos" element={<ReembolsosPage />} />
                {/* Política de cookies de Clerk (PostHog, la sesión, afiliados). */}
                <Route path="/cookies" element={<CookiesPage />} />
                {/* Política de privacidad de la extensión de Chrome: la URL de su ficha en la tienda. */}
                <Route path="/privacidad-extension" element={<PrivacidadExtensionPage />} />
                {/* Condiciones del plan Corporativo de Clerk: las registra su pago. */}
                <Route path="/condiciones-corporativo" element={<CondicionesCorporativoPage />} />
              </Routes>
            </Suspense>
          </main>
          <Footer />
        </div>
      </Router>
    </LanguageProvider>
  );
}
